/**
 * Selective resume import merge. Mirrors backend/src/utils/resumeImport.js.
 */

const CONTACT_FIELDS = ['name', 'email', 'phone', 'location', 'linkedin'];

const LIST_FIELDS = {
  education: ['degree', 'school', 'dates'],
  certifications: ['name', 'issuer', 'date'],
  languages: ['language', 'proficiency'],
  projects: ['name', 'description', 'technologies'],
};

function hasText(value) {
  return String(value ?? '').trim().length > 0;
}

function rowHasContent(row, fields) {
  return fields.some((field) => {
    const value = row?.[field];
    if (Array.isArray(value)) return value.some((item) => hasText(item));
    return hasText(value);
  });
}

function experienceHasContent(role) {
  return rowHasContent(role, ['title', 'company', 'location', 'dates'])
    || (role?.bullets || []).some((bullet) => hasText(bullet));
}

function cloneRow(row, id) {
  const next = { ...(row || {}), id };
  if (Array.isArray(row?.bullets)) next.bullets = [...row.bullets];
  if (Array.isArray(row?.technologies)) next.technologies = [...row.technologies];
  return next;
}

function appendList(current, incoming, fields, makeId) {
  const rows = (incoming || []).filter((row) => rowHasContent(row, fields));
  return [...(current || []), ...rows.map((row) => cloneRow(row, makeId()))];
}

export function importChoiceKey(section) {
  return section?.id ? `${section.key}:${section.id}` : section.key;
}

export function selectionFromImport(sections, selected) {
  const chosen = selected instanceof Set ? selected : new Set(selected || []);
  const selection = {
    contact: false,
    summary: false,
    education: false,
    skills: false,
    certifications: false,
    languages: false,
    projects: false,
    experienceIds: [],
    customIds: [],
  };
  for (const section of sections || []) {
    if (!chosen.has(importChoiceKey(section))) continue;
    if (section.key === 'experience') selection.experienceIds.push(section.id);
    else if (section.key === 'custom') selection.customIds.push(section.id);
    else if (Object.prototype.hasOwnProperty.call(selection, section.key)) selection[section.key] = true;
  }
  return selection;
}

export function mergeImportedResume(current, imported, selection = {}, ids) {
  const base = current || {};
  const incoming = imported || {};
  const nextId = ids;
  const next = {
    ...base,
    contact: { ...(base.contact || {}) },
    section_labels: { ...(base.section_labels || {}) },
    summary: base.summary || '',
    experience: [...(base.experience || [])],
    education: [...(base.education || [])],
    skills: [...(base.skills || [])],
    certifications: [...(base.certifications || [])],
    languages: [...(base.languages || [])],
    projects: [...(base.projects || [])],
    links: [...(base.links || [])],
    custom_sections: [...(base.custom_sections || [])],
  };

  if (selection.contact) {
    for (const field of CONTACT_FIELDS) {
      const value = String(incoming.contact?.[field] ?? '').trim();
      if (value) next.contact[field] = value;
    }
  }

  if (selection.summary && hasText(incoming.summary)) {
    next.summary = String(incoming.summary).trim();
  }

  const experienceIds = new Set(selection.experienceIds || []);
  for (const role of incoming.experience || []) {
    if (!experienceIds.has(role.id) || !experienceHasContent(role)) continue;
    next.experience.push(cloneRow(role, nextId('exp')));
  }

  if (selection.education) {
    next.education = appendList(next.education, incoming.education, LIST_FIELDS.education, () => nextId('edu'));
  }
  if (selection.certifications) {
    next.certifications = appendList(next.certifications, incoming.certifications, LIST_FIELDS.certifications, () => nextId('cert'));
  }
  if (selection.languages) {
    next.languages = appendList(next.languages, incoming.languages, LIST_FIELDS.languages, () => nextId('lang'));
  }
  if (selection.projects) {
    next.projects = appendList(next.projects, incoming.projects, LIST_FIELDS.projects, () => nextId('proj'));
  }

  if (selection.skills) {
    const seen = new Set(
      next.skills.map((skill) => String(skill).trim().toLowerCase()).filter(Boolean)
    );
    for (const skill of incoming.skills || []) {
      const trimmed = String(skill).trim();
      if (!trimmed) continue;
      const key = trimmed.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      next.skills.push(trimmed);
    }
  }

  const customIds = new Set(selection.customIds || []);
  for (const section of incoming.custom_sections || []) {
    if (!customIds.has(section.id)) continue;
    if (!hasText(section.label) && !hasText(section.body)) continue;
    next.custom_sections.push(cloneRow(section, nextId('sec')));
  }

  return next;
}
